import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService } from '../services/auth';
import { authEvents } from '../services/authEvents';
import {
  clearActivityDebounce,
  getSessionCheckIntervalMs,
  LAST_ACTIVITY_KEY,
  recordActivityDebounced,
  runSessionMaintenance,
} from '../services/sessionManager';
import { User } from '../types';

interface AuthContextType {
  user: User | null;
  login: (phone: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'click',
  'scroll',
  'touchstart',
] as const;

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const sessionValid = authService.initSession();
    if (sessionValid) {
      const storedUser = authService.getUser();
      if (storedUser) {
        setUser(storedUser);
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    return authEvents.onUnauthorized(() => {
      setUser(null);
    });
  }, []);

  useEffect(() => {
    if (!user) return;

    const onActivity = () => recordActivityDebounced();
    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, onActivity, { passive: true });
    });

    void runSessionMaintenance();

    const intervalId = window.setInterval(() => {
      void runSessionMaintenance();
    }, getSessionCheckIntervalMs());

    const onStorage = (event: StorageEvent) => {
      if (event.key === 'token' && !localStorage.getItem('token')) {
        setUser(null);
      }
      if (event.key === LAST_ACTIVITY_KEY) {
        void runSessionMaintenance();
      }
    };
    window.addEventListener('storage', onStorage);

    return () => {
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, onActivity);
      });
      window.clearInterval(intervalId);
      clearActivityDebounce();
      window.removeEventListener('storage', onStorage);
    };
  }, [user]);

  const login = async (phone: string, password: string) => {
    const response = await authService.login({ phone, password });
    authService.setToken(response.token);
    authService.setUser(response.user);
    authService.touchSession();
    setUser(response.user);
  };

  const logout = async () => {
    await authService.logout();
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        isAuthenticated: !!user,
        loading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
